import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Search,
  // FilterList,
  Visibility,
  Warning,
  Timer,
  Refresh
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

interface Incident {
  id: number;
  incident_id: string;
  status: string;
  severity: string;
  violation_type: string;
  security_rule_name: string;
  compliance_framework: string;
  assignee_id: number | null;
  created_at: string;
  sla_deadline: string | null;
  sla_breach: boolean;
}

const IncidentList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [severityFilter, setSeverityFilter] = useState<string>(
    searchParams.get('severity') || 'all'
  );
  const slaBreachParam = searchParams.get('sla_breach');
  const assignedToMeParam = searchParams.get('assigned_to_me');

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: [
      'incidents',
      page,
      rowsPerPage,
      search,
      statusFilter,
      severityFilter,
      slaBreachParam,
      assignedToMeParam
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      if (slaBreachParam === 'true') params.append('sla_breach', 'true');
      if (assignedToMeParam === 'true') params.append('assigned_to_me', 'true');

      const response = await axios.get(`/api/incidents?${params.toString()}`);
      return response.data;
    },
    refetchInterval: 15000 // auto-refresh every 15 seconds
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'error';
      case 'assigned':
        return 'warning';
      case 'investigating':
        return 'info';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const isSLAWarning = (incident: Incident) => {
    if (!incident.sla_deadline || incident.sla_breach) return false;
    const deadline = new Date(incident.sla_deadline);
    const now = new Date();
    const timeRemaining = deadline.getTime() - now.getTime();
    const hoursRemaining = timeRemaining / (1000 * 60 * 60);
    return hoursRemaining < 4 && hoursRemaining > 0;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load incidents. Please try again.</Alert>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Incidents
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
          >
            {data?.pagination?.total ?? 0} total incidents
            {isRefetching && <CircularProgress size={12} thickness={6} />}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isRefetching}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" onClick={() => navigate('/incidents/dashboard')}>
            View Dashboard
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              placeholder="Search incidents..."
              value={search}
              onChange={handleSearchChange}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="investigating">Investigating</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>

            <TextField
              select
              label="Severity"
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Severities</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </TextField>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Incident ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Violation Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Severity</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Framework</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created</strong>
                  </TableCell>
                  <TableCell>
                    <strong>SLA</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No incidents found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.data?.map((incident: Incident) => (
                    <TableRow
                      key={incident.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/incidents/${incident.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {incident.incident_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{incident.violation_type}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {incident.security_rule_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={incident.severity.toUpperCase()}
                          size="small"
                          color={getSeverityColor(incident.severity) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={incident.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(incident.status) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {incident.compliance_framework || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(incident.created_at), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(incident.created_at), 'HH:mm')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {incident.sla_breach ? (
                          <Tooltip title="SLA Breached">
                            <Chip icon={<Warning />} label="BREACH" size="small" color="error" />
                          </Tooltip>
                        ) : isSLAWarning(incident) ? (
                          <Tooltip title="SLA Warning">
                            <Chip icon={<Timer />} label="WARNING" size="small" color="warning" />
                          </Tooltip>
                        ) : (
                          <Chip label="OK" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/incidents/${incident.id}`);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={data?.pagination?.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default IncidentList;
